import { View } from "react-native";
import { ButtonIcon, ButtonLink, ButtonText } from "ui/gluestack/button";
import { AddIcon } from "ui/gluestack/icon";
import { ProfileAvatar } from "app/components/atoms/ProfileAvatar";
import TableList from "app/components/organisms/TableList";
import Pagination from "ui/molecules/pagination/Pagination";
import CardItem from "app/components/organisms/CardItem";
import ResidentContent from "../organisms/ResidentContent";
import ResidentActions from "../organisms/ResidentActions";
import DateAdmittedContent from "../organisms/DateAdmittedContent";
import { ResidentModel } from "app/services/api/types/residents/models/ResidentModel";
import { ColumnModel } from "app/services/api/types/common/models/ColumnModel";
import {
  StatusIndicatorMapping,
  StatusTitleMapping,
} from "app/services/api/types/residents/enums/ResidentStatusEnum";
import StatusIndicator from "ui/atoms/avatar/statusIndicator/StatusIndicator";
import { useResidentListingWithOffline } from "app/features/offline/hooks/useResidentsListingWithOffline";
import { PermissionCheck } from "app/features/permission/atoms/PermissionCheck";
import { PermissionsEnums } from "app/services/api/types/authorisation/enums/PermissionsEnums";
import { Portal } from "ui/modules/ThrelsPortal/Portal";
import { AddResidentPath, ResidentsPath } from "../../../constants/routePaths";
import StatusFlagContent from "../organisms/StatusFlagContent";
import { SearchField } from "../molecules/SearchField";
import { ResidentFilterComponent } from "../molecules/ResidentFilterComponent";
import { ErrorBoundary } from "@sentry/react-native";
import { ResidentsFilterProvider } from "../context/ResidentsFilterContext";
import ViewMore from "ui/molecules/common/ViewMore";
import { DependencyIndicator } from "../atoms/DependencyIndicator";

function ResidentsListContent() {
  const { data, isFetching, handlePageChange } =
    useResidentListingWithOffline();

  const columns: ColumnModel<ResidentModel>[] = [
    {
      title: "Name",
      dataKey: "first_name",
      renderCell: (resident: ResidentModel) => (
        <View className="flex flex-row items-center">
          <ProfileAvatar resident={resident} />
        </View>
      ),
      minWidth: 150,
    },
    {
      title: "Status",
      dataKey: "status",
      renderCell: (resident: ResidentModel) => (
        <StatusIndicator
          status={StatusIndicatorMapping[resident.status]}
          statusTitle={StatusTitleMapping[resident.status]}
        />
      ),
    },
    {
      title: "Dependency",
      dataKey: "dependency",
      renderCell: (resident: ResidentModel) => (
        <View className="flex flex-row items-start">
          <DependencyIndicator dependency={resident.dependency} />
        </View>
      ),
    },
    {
      title: "Status flags",
      dataKey: "status_flags",
      renderCell: (resident: ResidentModel) => (
        <StatusFlagContent resident={resident} />
      ),
    },
    {
      title: "Check in",
      dataKey: "admission_start_date",
      renderCell: (resident: ResidentModel) => (
        <DateAdmittedContent
          date={resident.admission_start_date}
          resident={resident}
        />
      ),
    },
    {
      title: "",
      renderCell: (resident: ResidentModel) => {
        return (
          <View className="flex flex-row items-center justify-end">
            <ViewMore />
          </View>
        );
      },
    },
  ];

  return (
    <View className="flex-1 bg-white px-dashboard">
      <Portal hostName="headerButton" identifier="residentList">
        <PermissionCheck
          permission={PermissionsEnums.CREATE_RESIDENT}
          fallback={<></>}
        >
          <ButtonLink
            href={AddResidentPath}
            variant="solid"
            action="primary"
            size="sm"
          >
            <ButtonText>Add new resident</ButtonText>
            <ButtonIcon as={AddIcon} className="w-4 h-4" />
          </ButtonLink>
        </PermissionCheck>
      </Portal>
      <ErrorBoundary>
        <View className="flex flex-row justify-end pb-2 gap-2">
          <SearchField />
          <ResidentFilterComponent />
        </View>
      </ErrorBoundary>
      <TableList
        columns={columns}
        data={data?.data || []}
        isFetching={isFetching}
        emptyMessage="No residents available."
        clickablePath={(resident) => `${ResidentsPath}/${resident.id}/handover`}
        mobileRenderItem={(item) => (
          <CardItem
            key={item.id}
            actionButtons={<ResidentActions resident={item} />}
            cardContent={<ResidentContent resident={item} />}
            href={`${ResidentsPath}/${item.id}/handover`}
          />
        )}
      />

      <Pagination meta={data?.meta} onPageChange={handlePageChange} />
    </View>
  );
}

export function ResidentsListTemplate() {
  return (
    <ResidentsFilterProvider>
      <ResidentsListContent />
    </ResidentsFilterProvider>
  );
}
